<script setup lang="ts">
import { onMounted, provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useClipboard } from '@vueuse/core'
import { useToast } from 'primevue/usetoast'
import { FlowState } from '../constants/flowState'
import { facetsBaseModel } from '../constants/productFacetsBaseModel'
import BaseModelsListSingle from '../components/BaseModelsListSingle.vue'
import SearchListSidebar from '../components/SearchListSidebar.vue'
import { useBaseModelsStore } from '../stores/BaseModelsStore'

const route = useRoute()
const store: any = useBaseModelsStore()
const toast = useToast()

provide('search', {
  facets: facetsBaseModel,
  store
})

// HEADER
useHead({
  title: 'Search Base Models',
  meta: [
    {
      name: 'description',
      content: 'Search models we stock and can special order'
    }
  ]
})

// Clipboard functionality
const clipboardValue = ref()
const { copy, copied, isSupported } = useClipboard({ source: clipboardValue, copiedDuring: 1600 })

// Refactor to composable after adding another list
function copyUrlToClipboard() {
  const urlSegments = [
    'https://dascosales.com',
    '/products/base-models',
    `?${store.searchQueryUrl}`
  ]

  const url = urlSegments.join('')
  clipboardValue.value = url
  copy(clipboardValue.value)
}

watch(copied, (newVal, _oldVal) => {
  if (newVal) {
    toast.add({
      detail: 'Copied link to clipboard',
      life: 2700,
      severity: 'info'
    })
  }
})

onMounted(async () => {
  const routeQuery = route.query || {}
  if (Object.keys(routeQuery)?.length)
    store.searchQueryBuildFromUrl(routeQuery)

  await store.getTypesenseResults()
})
</script>

<template>
  <div class="mb-6 flex align-items-center justify-content-between">
    <h1 class="text-primary-800 m-0 flex align-items-center">
      Products - Base Models
      <span
        v-if="store.flow === FlowState.Loading"
        class="ml-2 font-normal text-400"
      >
        Loading
        <fa-icon
          icon="fa-solid fa-circle-notch"
          class="fa-spin"
        />
      </span>
      <span
        v-else
        class="ml-2 font-normal text-lg"
      >
        Results <span v-if="store.searchParameters.query?.length">for "{{ store.searchParameters.query }}" </span>
        ({{ store.tsResults.found }})
      </span>
    </h1>
    <div>
      <p-btn
        v-if="isSupported"
        class="p-button-sm p-button-secondary mx-1"
        @click="copyUrlToClipboard()"
      >
        <fa-icon
          icon="fa fa-external-link"
          class="text-400 mr-2"
        /> Share Search
      </p-btn>
    </div>
  </div>
  <section class="p-2 surface-section border-round">
    <div class="grid">
      <search-list-sidebar key="base-models" />
      <section class="col-12 lg:col-9">
        <div v-if="store.flow === FlowState.Loading">
          LOADING
        </div>
        <div v-else-if="store.flow === FlowState.Idle && !store.hits?.length">
          <div class="m-4 p-4 flex align-items-center justify-content-center text-3xl text-400">
            There are no results for your search.
          </div>
        </div>
        <div v-else-if="store.flow === FlowState.Idle && store.hits?.length > 0">
          <template
            v-for="i in store.hits"
            :key="i.document.id"
          >
            <base-models-list-single
              class="h-full"
              :i="i.document"
            />
          </template>
          <div class="w-full flex align-items-center justify-content-center border-round border-1 border-200 bg-gray-50 p-2 lg:p-4">
            <p-btn
              v-show="store.tsResults.found > store.hits.length"
              class="p-button-text"
              :disabled="store.flow === FlowState.LoadingMore"
              label="Load More"
              @click="store.getTypesenseResults({ loadMore: true })"
            />
            <div
              v-if="store.flow === FlowState.LoadingMore"
            >
              <fa-icon
                icon="fa fa-sync"
                class="fa-spin"
              />
            </div>
            <div class="text-xl lg:text-2xl ml-2 font-italic font-light text-600">
              {{ store.hits.length }} out of {{ store.tsResults.found }} Results
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>
